// Distinct living signatures for each venture — every row of the index opens
// into its own kind of motion. Pure CSS/JS animation; no server needed.

import { useEffect, useState } from "react";
import MotionVideo from "@/components/MotionVideo";
import { MOTION_ART } from "@/lib/motion";

// MUSIC — a rotating wall of the catalogue: five different albums' motion art
// playing side by side, each advancing to the next album on its own clock.
export const MusicBand = () => {
  const [order] = useState(() => {
    const idx = MOTION_ART.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idx[i], idx[j]] = [idx[j], idx[i]];
    }
    return idx;
  });
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStep((s) => s + 1), 9000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex h-full items-stretch justify-center gap-2">
      {[0, 1, 2, 3, 4].map((k) => (
        <MotionVideo
          key={`${k}-${order[(step + k * 5) % order.length]}`}
          name={MOTION_ART[order[(step + k * 5) % order.length]]}
          autoPlay
          className={`h-full w-auto object-contain transition-opacity duration-700 ${k === 2 ? "" : "opacity-70"}`}
        />
      ))}
    </div>
  );
};

// FILM & TV — Highgate key art in a slow Ken Burns pan
export const FilmBand = () => (
  <div className="relative h-full w-full overflow-hidden">
    {["/filmtv/night-manager.webp", "/filmtv/naina.webp", "/filmtv/wheel.webp"].map((src, i) => (
      <img
        key={src}
        src={src}
        alt=""
        className="kenburns-pan absolute inset-0 h-full w-full object-cover"
        style={{ animationDelay: `${i * 7}s`, objectPosition: "center 22%" }}
      />
    ))}
  </div>
);

// DESIGNS — her real Amrita Sen Designs pieces gliding past
export const DesignsBand = () => {
  const imgs = [
    "CAPL2750BZ_1-600x600.jpg", "CAPL2750_2-600x600.jpg", "CAPL2659BZ_1-600x600.jpg",
    "CAPL2659_2-600x600.jpg", "CAPL2692BZ_1-600x600.jpg", "CAPL2692_2-600x600.jpg",
    "CAPL1419_1-600x600.webp", "CAPL1418_1-600x600.webp",
  ];
  return (
    <div className="flex h-full items-center overflow-hidden">
      <div className="band-marquee flex gap-3">
        {[...imgs, ...imgs].map((n, i) => (
          <img key={i} src={`/designs/${n}`} alt="" className="h-24 w-auto shrink-0 md:h-32" />
        ))}
      </div>
    </div>
  );
};

// BOLLYDOLL — the BollyWorld art collages gliding past
export const BollydollBand = () => {
  const pages = ["01", "07", "14", "20", "27", "33"].map((n) => `/books/pages/bollydoll-${n}.webp`);
  return (
    <div className="flex h-full items-center overflow-hidden">
      <div className="band-marquee flex gap-3" style={{ "--band-w": "160px" }}>
        {[...pages, ...pages].map((src, i) => (
          <img key={i} src={src} alt="" className="h-24 w-auto shrink-0 md:h-32" />
        ))}
      </div>
    </div>
  );
};

// MYSTMUSIC — the real Tuscan campus gliding past: studios, bar, courtyard
export const MystBand = () => (
  <div className="flex h-full items-center overflow-hidden">
    <div className="band-marquee flex gap-3">
      {Array.from({ length: 16 }, (_, i) => (
        <img key={i} src={`/myst/myst-${i % 8}.jpg`} alt="" className="h-24 w-auto shrink-0 md:h-32" />
      ))}
    </div>
  </div>
);

// BOOKS — a stream of illustrated pages filling the whole width
export const BooksBand = () => {
  const pages = [
    ...["07", "13", "19", "25", "31", "37"].map((n) => `/books/pages/cosmic-${n}.webp`),
    ...["13", "25", "49", "61"].map((n) => `/books/pages/awakened-${n}.webp`),
  ];
  return (
    <div className="flex h-full items-center overflow-hidden">
      <div className="band-marquee flex gap-2">
        {[...pages, ...pages].map((src, i) => (
          <img key={i} src={src} alt="" className="h-24 w-auto shrink-0 md:h-32" />
        ))}
      </div>
    </div>
  );
};

// PRESS — a slow golden sheen sweeping a quiet quote line
export const PressBand = () => (
  <div className="relative flex h-full items-center justify-center overflow-hidden">
    <span
      className="font-serif-display press-sheen text-sm italic md:text-lg"
      style={{ letterSpacing: "0.2em" }}
    >
      Rolling Stone · The Hollywood Reporter · Variety · Deadline · Scroll.in
    </span>
  </div>
);

// CONTACT — a single rosette breathing in gold
export const ContactBand = () => (
  <div className="flex h-full items-center justify-center" aria-hidden="true">
    <svg viewBox="0 0 60 60" className="rosette-breathe h-14 w-14 text-[#d4af37]" fill="none" stroke="currentColor" strokeWidth="1.2">
      {Array.from({ length: 8 }, (_, p) => (
        <ellipse key={p} cx="30" cy="16" rx="4" ry="9" transform={`rotate(${p * 45} 30 30)`} />
      ))}
      <circle cx="30" cy="30" r="2.4" fill="currentColor" stroke="none" />
    </svg>
  </div>
);
